import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { StorageKey } from '../storage/models/storage-key.enum';
import { ProfileModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { SecureStorageService } from '../storage/secure-storage.service';
import { PromptService } from '../promtp-service/prompt.service';
import { EventService } from '../events/event.service';
import { differenceInYears } from 'date-fns';
import { VitalScoreApiConfiguration } from 'src/app/api/proxy/vitalscore/vital-score-api-configuration';
import { profileGetProfileGet, profileSaveProfilePost } from 'src/app/api/proxy/vitalscore/functions';
import { Network } from '@capacitor/network';
import { BaseApiService } from '../base-api-service/base-api.service';
import { normalizeMobileNumberE164 } from 'src/app/utilities/phone-number/phone-number.util';
import { filter, map, timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { RequestBuilder } from 'src/app/api/proxy/vitalscore/request-builder';
import { VitalScoreStrictHttpResponse } from 'src/app/api/proxy/vitalscore/vital-score-strict-http-response';
import { LocaleService } from '../locale-service/locale.service';


@Injectable({
  providedIn: 'root',
})
export class MyProfileService extends BaseApiService {

  private NETWORK_TIMEOUT = 25000;

  constructor(
    protected _PromptService: PromptService,
    private http: HttpClient,
    private vitalScoreCfg: VitalScoreApiConfiguration,
    private _localeService: LocaleService,
  ) {
    super(_PromptService);
  }
  private blocking = false;


  public async GetProfileStatic(): Promise<ProfileModel | null> {
    const myProfile = await SecureStorageService.get(StorageKey.MyProfile);
    let model: ProfileModel | null = null;
    if (myProfile) {
      try {
        model = JSON.parse(myProfile);
      } catch {
        model = null
      }
    }

    if (model) {
      // Ensure heightInCm is a number when loading from storage
      if (model.heightInCm != null && model.heightInCm != undefined && model.heightInCm > 0) {
        model.heightInCm = Number(model.heightInCm);
      }
      // Ensure weightInKg is a number when loading from storage
      if (model.weightInKg != null && model.weightInKg != undefined && model.weightInKg > 0) {
        model.weightInKg = Number(model.weightInKg);
      }
      EventService.myProfileUpdated.emit(model);
      await this.syncLocaleFromProfile(model);
    }

    return model;
  }

  public async GetProfile(forced: boolean = false, syncLocaleOnFetch: boolean = true): Promise<ProfileModel | null> {
    const myProfile = await SecureStorageService.get(StorageKey.MyProfile);
    let model = await this.GetProfileStatic();

    const now = new Date().getTime();
    const oneDay = 60 * 60 * 1000 * 2; // 2 hour in milliseconds
    const lastFetchTime = await SecureStorageService.get(StorageKey.LastProfileRefresh);

    if (!lastFetchTime || // No fetch has been made
      now - parseInt(lastFetchTime) > oneDay // More than 12 hour has passed      
    ) {
      forced = true;
    }

    if (model) {
      if (!model.firstName || model.firstName.length == 0)
        forced = true;
      if (!model.dateOfBirth || model.dateOfBirth.length == 0)
        forced = true;

      if (model.gender == null) {
        forced = true;
      }
      EventService.myProfileUpdated.emit(model);

      if (!forced) {
        return model;
      }
    }

    const status = await Network.getStatus();
    if (!status.connected) {
      return null;
    }

    const obs = profileGetProfileGet(this.http, this.vitalScoreCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'GetProfile');
    const result = httpResponse ? httpResponse.body : null;

    if (result) {
      // Ensure heightInCm is a number when fetching from server
      if (result.heightInCm != null && result.heightInCm != undefined && result.heightInCm > 0) {
        result.heightInCm = Number(result.heightInCm);
      }

      // Ensure weightInKg is a number when fetching from server
      if (result.weightInKg != null && result.weightInKg != undefined && result.weightInKg > 0) {
        result.weightInKg = Number(result.weightInKg);
      }

      if (!syncLocaleOnFetch && model && model.languageCode) {
        result.languageCode = model.languageCode;
      }

      model = result;
      await SecureStorageService.set(StorageKey.MyProfile, JSON.stringify(result));
      await SecureStorageService.set(StorageKey.LastProfileRefresh, now.toString());
    }


    if (model)
      EventService.myProfileUpdated.emit(model);

    if (model && syncLocaleOnFetch) {
      await this.syncLocaleFromProfile(model);
    }

    return model;
  }

  private async syncLocaleFromProfile(profile: ProfileModel): Promise<void> {
    if (profile.languageCode) {
      await this._localeService.applyFromProfileLanguage(profile.languageCode);
    }
  }

  public async SaveProfile(profile: ProfileModel): Promise<boolean> {

    if (profile) {
      // Ensure heightInCm is a number
      if (profile.heightInCm != null && profile.heightInCm != undefined && profile.heightInCm > 0) {
        profile.heightInCm = Number(profile.heightInCm);
      }

      // Ensure weightInKg is a number
      if (profile.weightInKg != null && profile.weightInKg != undefined && profile.weightInKg > 0) {
        profile.weightInKg = Number(profile.weightInKg);
      }

      profile.mobileNumber = normalizeMobileNumberE164(profile.mobileNumber);
    }

    const obs = profileSaveProfilePost(this.http, this.vitalScoreCfg.rootUrl, { body: profile });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'SaveProfile');
    const result = httpResponse ? httpResponse.body : null;

    if (!result) {
      return false;
    }

    await SecureStorageService.set(StorageKey.MyProfile, JSON.stringify(result));

    const now = new Date().getTime();
    await SecureStorageService.set(StorageKey.LastProfileRefresh, now.toString());
    EventService.myProfileUpdated.emit(result);
    return true;
  }

  public getAge(profile: ProfileModel): number {
    return differenceInYears(new Date(), new Date(profile.dateOfBirth));
  }

  /**
   * Records a foreground dashboard visit (LastAppAccess + optional UserAppVisit points).
   * Call only from health-score / hwh-dashboard / pn-dashboard enter — not home shell or background sync.
   * At most once per hour (persisted). Fire-and-forget: failures are ignored.
   * Direct HTTP until the VitalScore OpenAPI proxy is regenerated with RecordAppVisit.
   */
  public async recordAppVisit(): Promise<void> {
    try {
      if (!(await this.shouldRecordAppVisit())) {
        return;
      }

      const status = await Network.getStatus();
      if (!status.connected) {
        return;
      }

      const rb = new RequestBuilder(this.vitalScoreCfg.rootUrl, '/Profile/RecordAppVisit', 'post');
      const obs = this.http.request(
        rb.build({ responseType: 'json', accept: 'application/json' }),
      ).pipe(
        filter((r: unknown): r is HttpResponse<unknown> => r instanceof HttpResponse),
        map((r: HttpResponse<unknown>) =>
          r.clone({ body: String(r.body) === 'true' }) as VitalScoreStrictHttpResponse<boolean>,
        ),
        timeout(this.NETWORK_TIMEOUT),
      );

      await firstValueFrom(obs);
      await SecureStorageService.set(StorageKey.LastRecordAppVisit, new Date().toISOString());
    } catch {
      // Non-blocking engagement call — do not surface network errors on home load.
    }
  }

  private async shouldRecordAppVisit(): Promise<boolean> {
    const lastVisitRaw = await SecureStorageService.get(StorageKey.LastRecordAppVisit);
    if (!lastVisitRaw) {
      return true;
    }
    const lastVisitMs = Date.parse(lastVisitRaw);
    if (Number.isNaN(lastVisitMs)) {
      return true;
    }
    const oneHourMs = 60 * 60 * 1000;
    return Date.now() - lastVisitMs >= oneHourMs;
  }

}
